import dayjs from 'dayjs'
import {
  addMethod,
  array,
  bool,
  boolean,
  date,
  InferType,
  mixed,
  number,
  object,
  SchemaOf,
  string,
} from 'yup'

import { t } from '../../i18n'
import { SignUpFormType } from '../types/interface' // Import form type definition

const emailDefinition = string()
  .email('Invalid email address')
  .required('Required')

const passwordDefinition = string()
  .min(8, 'Must be at least 8 characters')
  .matches(
    /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/,
    'Must contain at least 1 special character.'
  )
  .required('Required')

const firstNameDefinition = string().required(
  t('validations.firstname_required')
)

const lastNameDefinition = string().required(t('validations.lastname_required'))

const countryDefinition = number()
  .required(t('validations.country_required'))
  .positive()
  .integer()

const cityDefinition = number()
  .required(t('validations.city_required'))
  .positive()
  .integer()

const provinceDefinition = number()
  .required(t('validations.province_required'))
  .positive()
  .integer()

export const emailSchema = object({
  email: emailDefinition,
})

export const newPassSchema = object({
  code: string()
    .matches(/^[0-9]{6}$/, 'Code must be 6 digits')
    .required('Required'),
  password: passwordDefinition,
})

export const signInSchema = object({
  email: emailDefinition,
  password: passwordDefinition,
})

export const forcePasswordSchema = object({
  email: emailDefinition,
  newPassword: passwordDefinition,
  password: passwordDefinition,
})

export const signUpSchema: SchemaOf<SignUpFormType> = object({
  city: cityDefinition,
  country: countryDefinition,
  email: emailDefinition,
  firstName: firstNameDefinition,
  lastName: lastNameDefinition,
  password: passwordDefinition,
  province: provinceDefinition,
})

export const personalInfoSchema = object({
  address: string().required(t('validations.required_field')),
  city: cityDefinition,
  country: countryDefinition,
  dateOfBirth: date().required(t('validations.required_field')).nullable(),
  firstName: firstNameDefinition,
  lastName: lastNameDefinition,
  phoneNumber: string()
    .typeError(t('validations.valid_number_required'))
    .test(
      'phone-digits-count',
      t('validations.valid_number_required'),
      (value) => {
        if (!value) return false
        const digitsOnly = value.replace(/\D/g, '')
        return digitsOnly.length >= 11 && digitsOnly.length <= 12
      }
    )
    .required(t('validations.required_field')),
})

export const licenseInfoSchema = object({
  back: string().required(t('validations.back_side_required')),
  docExpirationDate: date().when('countryId', {
    is: (countryId: number) => countryId === 3,
    otherwise: (schema) => schema.required(t('validations.required_field')),
    then: (schema) => schema.notRequired(),
  }),
  front: string().required(t('validations.front_side_required')),
  licenseNumber: string().required(t('validations.required_field')),
  type: mixed().required(t('validations.license_type_required')),
})

export const workInfoSchema = object({
  docExpirationDate: date().nullable(),
  document: mixed().required('Please upload a work permit'),
})

export const vehicleOwnerInfoSchema = object({
  document: mixed().required("Please upload a vehicle owner's document."),
})

export const insuranceInfoSchema = object({
  docExpirationDate: date().required('Required').nullable(),
  document: mixed().required('Please upload your vehicle insurance document.'),
})

export const vehicleSafetyInfoSchema = object({
  docExpirationDate: date()
    .required(
      'Required. Remember to set expiry date to 1 year from inspection date.'
    )
    .nullable(),
  document: mixed().required('Please upload your vehicle safety document.'),
})

export const profilePictureInfoSchema = object({
  profile: string().required('Please upload your profile picture.').nullable(),
})

export const profilePictureSchema = object({
  picture: string().required('Please upload your profile picture.').nullable(),
})

export const driverAbstractSchema = object({
  document: mixed().required('Please upload the required document.'),
})

export const genericDocSchema = object({
  docExpirationDate: date().required('Required').nullable(),
  document: mixed().required('Please upload the required document.'),
})

export const DriverAgreementSchema = object({
  agreementAccepted: boolean(),
  required: boolean(),
})

export const vehiclePhotoSchema = object({
  back: string()
    .nullable()
    .required('Please upload the back side of your vehicle'),
  back_seats: string()
    .nullable()
    .required('Please upload the back seats of your vehicle'),
  front: string()
    .nullable()
    .required('Please upload the front of your vehicle'),
  front_seats: string()
    .nullable()
    .required('Please upload the front seats of your vehicle'),
  left: string()
    .nullable()
    .required('Please upload the left side of your vehicle'),
  odometer: string()
    .nullable()
    .required('Please upload the odometer of your vehicle'),
  right: string()
    .nullable()
    .required('Please upload the right side of your vehicle'),
  trunk: string()
    .nullable()
    .required('Please upload the trunk of your vehicle'),
})

export const additionalDocumentSchema = object({
  custom_name: string().required('Please select the document want to upload.'),
  key1: string().nullable().required('Please upload the required document.'),
})

export const agreementSchema = object({
  checkbox: bool().oneOf(
    [true],
    'You must agree and accept the terms and conditions.'
  ),
  date: date().required('Required'),
  fullName: string().required('Required'),
})

export const shiftCommentSchema = object({
  // Schema definition for shift comment form
  comment: string().max(200).required('Required'),
})

addMethod(object, 'dayjs', function method(message) {
  return this.test('dayjs', message, (value) => {
    if (!value) {
      return true
    }
    return dayjs.isDayjs(value)
  })
})

export const shiftPreferenceSchema = object({
  // Shift Preference Schema Form
  timeframe: array().of(
    object({
      days: array()
        .of(number())
        .min(1, 'At least one day must be selected.')
        .required('Days are required.'),
      end_time: date().nullable().required('End time is required.'),
      id: number().nullable(),
      start_time: date().nullable().required('Start time is required.'),
      status: string().required('Status is required.'),
    })
  ),
})

export type ShiftPreferenceForm = InferType<typeof shiftPreferenceSchema>

type GetGenericStepSchemaProps = {
  hasExpirationDate: boolean
  isRequired: boolean
}

export const getGenericStepSchema = ({
  hasExpirationDate,
  isRequired,
}: GetGenericStepSchemaProps) =>
  object().shape(
    {
      docExpirationDate: (() => {
        let validation = date().nullable()

        if (hasExpirationDate && isRequired) {
          validation = validation.required(t('validations.required_field'))
        }

        if (hasExpirationDate) {
          validation = validation.when('document', {
            is: (value: unknown) => !!value,
            otherwise: (schema) => schema,
            then: (schema) => schema.required(t('validations.required_field')),
          })
        }

        return validation
      })(),
      document: (() => {
        let validation = string().nullable()

        if (isRequired) {
          validation = validation.required(t('validations.required_field'))
        }

        if (hasExpirationDate) {
          validation = validation.when('docExpirationDate', {
            is: (value: unknown) => !!value,
            otherwise: (schema) => schema,
            then: (schema) => schema.required(t('validations.required_field')),
          })
        }

        return validation
      })(),
    },
    [['docExpirationDate', 'document']]
  )

export type PersonalInfoSchemaType = InferType<typeof personalInfoSchema>
export type LicenseInfoSchemaType = InferType<typeof licenseInfoSchema>
export type ProfilePictureInfoSchemaType = InferType<
  typeof profilePictureInfoSchema
>
export type DriverAgreementSchemaType = InferType<typeof DriverAgreementSchema>
export type GenericInfoSchemaType = InferType<typeof genericDocSchema>
export type FormSchemaValue =
  | DriverAgreementSchemaType
  | GenericInfoSchemaType
  | LicenseInfoSchemaType
  | PersonalInfoSchemaType
  | ProfilePictureInfoSchemaType
